import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Award, Download, ExternalLink, BookOpen } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CertificatesPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchCertificates();
  }, [isAuthenticated]);

  const fetchCertificates = async () => {
    try {
      const res = await fetch(`${API}/certificates/my-certificates`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setCertificates(data);
      }
    } catch (error) {
      console.error('Error fetching certificates:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="certificates-page">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">
            Mes certificats
          </h1>
          <p className="text-slate-600 mt-2">
            Vos attestations de réussite aux formations AFFEA
          </p>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 gap-6">
            {[1, 2].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6 space-y-4">
                  <div className="h-6 bg-slate-200 rounded w-3/4" />
                  <div className="h-4 bg-slate-200 rounded w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : certificates.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Award className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                Aucun certificat pour le moment
              </h3>
              <p className="text-slate-600 mb-6">
                Complétez vos cours pour obtenir vos certificats de réussite
              </p>
              <Button onClick={() => navigate('/courses')} data-testid="browse-courses-btn">
                <BookOpen className="h-4 w-4 mr-2" />
                Découvrir les cours
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {certificates.map((cert) => (
              <Card 
                key={cert.certificate_id} 
                className="overflow-hidden card-hover"
                data-testid={`certificate-${cert.certificate_id}`}
              >
                <div className="bg-gradient-to-r from-slate-900 to-slate-800 p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-teal-500 mx-auto mb-4 flex items-center justify-center">
                    <Award className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-white mb-2">
                    Certificat de Réussite
                  </h3>
                  <p className="text-teal-400 text-sm">AFFEA - Académie Francophone</p>
                </div>
                <CardContent className="p-6 space-y-4">
                  <div>
                    <p className="text-sm text-slate-500">Formation</p>
                    <p className="font-medium text-slate-900">{cert.course_title}</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Délivré à</p>
                      <p className="font-medium text-slate-900">{cert.user_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Date</p>
                      <p className="font-medium text-slate-900">{formatDate(cert.issued_at)}</p>
                    </div>
                  </div>
                  <div className="pt-4 border-t">
                    <p className="text-xs text-slate-400 text-center">
                      N° {cert.certificate_number}
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => navigate(`/certificate/${cert.certificate_id}`)}
                    data-testid={`view-cert-${cert.certificate_id}`}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Voir le certificat
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CertificatesPage;
